import * as React from 'react'

import {
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Link as MuiLink
} from '@material-ui/core'
import { DeviceListQuery, DeviceListQueryVariables } from './__generated__/DeviceListQuery'

import { FormattedDateTime } from '../formatted-date-time'
import { TruncatedId } from '../truncated-id'
import { gql } from 'apollo-boost'
import moment from 'moment'
import { useQuery } from '@apollo/react-hooks'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import { DeviceListFilter } from './DeviceListFilter'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { CsvImportButton } from '../../ui/CsvImportButton'
import { useGetViewer } from '../../api/useGetViewer'
import { useUpdateDevice } from '../../api/useUpdateDevice'
import { FULL_ACCESS } from '../../constants'

type DeviceListPageProps = IDeviceListPageProps

interface IDeviceListPageProps {}

const QUERY = gql`
  query DeviceListQuery($page: Int!, $type: String, $perPage: Int, $yoctoStatus: String) {
    devices(page: $page, type: $type, perPage: $perPage, yoctoStatus: $yoctoStatus) {
      page
      count
      pageSize
      devices {
        id
        created
        type
        latestHeartbeat {
          created
          networks {
            type
            ssid
          }
        }
        model {
          id
          title
        }
        boards {
          id
        }
      }
    }
  }
`

export const DeviceListPage: React.FC<DeviceListPageProps> = props => {
  const perPage = 50
  const [page, setPage] = React.useState(0)

  const params = useParams<{ status?: string }>()
  const yoctoStatus = params.status

  const { data, loading } = useQuery<DeviceListQuery, DeviceListQueryVariables>(QUERY, {
    variables: {
      page,
      type: 'flagship',
      perPage,
      yoctoStatus: yoctoStatus ? yoctoStatus.replace(/ /g, '') : null
    }
  })

  const { data: viewerData } = useGetViewer()
  const canBatchUpdateModel = FULL_ACCESS.includes(
    viewerData?.viewer?.account?.emailAddress || 'NOT_EDITOR'
  )
  const [updateDevice] = useUpdateDevice()

  const totalCount = data?.devices.count || 0

  if (loading) {
    return <CircularProgress />
  }

  return (
    <>
      <FlexHorizontal spaceBetween>
        {yoctoStatus ? (
          <Typography variant='h4'>
            <MuiLink component={Link} to='/devices'>
              Devices
            </MuiLink>{' '}
            / Yocto Status / {yoctoStatus}
          </Typography>
        ) : (
          <Typography variant='h4'>Devices</Typography>
        )}

        <FlexHorizontal>
          <DeviceListFilter />
          {canBatchUpdateModel ? (
            <CsvImportButton
              mappings={['deviceId', 'deviceModelId']}
              mainButtonText='Batch Update Device Model'
              buttonText='Update All'
              handleImport={async records => {
                for (const record of records as { deviceId: string; deviceModelId: string }[]) {
                  await updateDevice({
                    variables: {
                      input: {
                        id: record.deviceId,
                        deviceModel: record.deviceModelId
                      }
                    }
                  })
                }
              }}
            />
          ) : null}
        </FlexHorizontal>
      </FlexHorizontal>
      <Spacer size='large' />
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Last Contact</TableCell>
                <TableCell>SSID</TableCell>
                <TableCell>Paired Board(s)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.devices.devices.map(device => (
                <TableRow key={device.id}>
                  <TableCell>
                    <TruncatedId value={device.id} path={`/devices/${device.id}`}></TruncatedId>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <FormattedDateTime value={parseInt(device.created)} format='MMM DD, YYYY h:mm a' />
                  </TableCell>
                  <TableCell>{device.model && device.model.title}</TableCell>
                  <TableCell>
                    {device.latestHeartbeat ? moment(device.latestHeartbeat?.created).fromNow() : <span>Never</span>}
                  </TableCell>
                  <TableCell>{device.latestHeartbeat?.networks.find(n => n.type === 'Wireless')?.ssid}</TableCell>
                  <TableCell>
                    {device.boards[0] ? (
                      <TruncatedId value={device.boards[0].id} path={`/boards/${device.boards[0].id}`} />
                    ) : (
                      '(none)'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          count={totalCount}
          rowsPerPage={perPage}
          page={page}
          onPageChange={(_, page) => setPage(page)}
          onChangeRowsPerPage={() => {}}
        />
      </Card>
    </>
  )
}
